/**
 * @file WarehousesList.tsx
 * @description Список складов на общем `ResponsiveList` (phase19.23, A6;
 * phase19.31 зона «Склад» — компактный список, разбор B «Склады»).
 *
 * Таблица от 1024: Склад (код подписью) · Тип · Адрес (график подписью) ·
 * Статус. Список до 1024: «Основной  [по умолчанию] / CHB-MAIN · Готовая
 * продукция / адрес». «По умолчанию» — метка без точки (это признак, а не
 * статус); «Активен / Неактивен» — статусом по тону.
 *
 * Действия строки (изменить, сделать основным, деактивировать) — меню «⋯»,
 * опасное последним.
 */

import { Pencil, Power, Star, Warehouse } from "lucide-react";

import { Badge } from "../ui/badge";
import type { ListColumn } from "../ui/responsive-list-types";
import { ResponsiveList } from "../ui/ResponsiveList";
import type { RowAction } from "../ui/RowActionsMenu";
import { StatusPill } from "../ui/StatusPill";
import { TwoLineCell } from "../ui/TwoLineCell";
import type { WarehouseDto } from "../../lib/api-wms";
import { warehouseKindLabel } from "../../lib/wms-warehouse-kind";

interface Props {
  items: WarehouseDto[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onEdit: (warehouse: WarehouseDto) => void;
  onSetDefault: (warehouse: WarehouseDto) => void;
  onDeactivate: (warehouse: WarehouseDto) => void;
}

/** Статус склада и метка «по умолчанию». */
function WarehouseState({ w }: { w: WarehouseDto }) {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {w.isActive ? <StatusPill tone="success">Активен</StatusPill> : <StatusPill tone="neutral">Неактивен</StatusPill>}
      {w.isDefault ? <Badge variant="outline">по умолчанию</Badge> : null}
    </span>
  );
}

/** «Готовая продукция · не для продажи». */
function kindLine(w: WarehouseDto): string {
  return [warehouseKindLabel(w.kind), w.isSellable === false ? "не для продажи" : null].filter(Boolean).join(" · ");
}

const COLUMNS: ListColumn<WarehouseDto>[] = [
  {
    key: "name",
    header: "Склад",
    width: "w-[240px]",
    cell: (w) => <TwoLineCell primary={w.name} secondary={<span className="font-mono">{w.code}</span>} />,
    compact: "title",
    compactCell: (w) => w.name,
  },
  {
    key: "kind",
    header: "Тип",
    width: "w-[200px]",
    cell: (w) => (
      <TwoLineCell primary={warehouseKindLabel(w.kind)} secondary={w.isSellable === false ? "не для продажи" : undefined} />
    ),
    compact: "subtitle",
    compactCell: (w) => (
      <>
        <span className="font-mono">{w.code}</span> · {kindLine(w)}
      </>
    ),
    // На 1024–1279 тип — третьей линией под названием и кодом.
    fold: { into: "name", cell: kindLine },
  },
  {
    key: "address",
    header: "Адрес",
    cell: (w) => (
      <TwoLineCell primary={w.address ?? "—"} secondary={w.workingHours ? `График: ${w.workingHours}` : undefined} />
    ),
    compact: "meta",
    compactCell: (w) => w.address ?? null,
  },
  {
    key: "state",
    header: "Статус",
    width: "w-[188px]",
    cell: (w) => <WarehouseState w={w} />,
    compact: "trailing",
    compactCell: (w) =>
      !w.isActive ? <StatusPill tone="neutral">Неактивен</StatusPill> : w.isDefault ? <Badge variant="outline">по умолчанию</Badge> : null,
  },
];

export function WarehousesList({ items, loading, error, onRetry, onEdit, onSetDefault, onDeactivate }: Props) {
  /** Реестр действий строки: новое действие = запись, а не ветка в разметке. */
  function actionsFor(w: WarehouseDto): RowAction[] {
    const actions: RowAction[] = [{ key: "edit", label: "Изменить", icon: Pencil, run: () => onEdit(w) }];
    if (!w.isDefault && w.isActive) {
      actions.push({ key: "default", label: "Сделать складом по умолчанию", icon: Star, run: () => onSetDefault(w) });
      actions.push({ key: "off", label: "Деактивировать", icon: Power, danger: true, run: () => onDeactivate(w) });
    }
    return actions;
  }

  return (
    <ResponsiveList
      aria-label="Склады магазина"
      rows={items}
      columns={COLUMNS}
      rowKey={(w) => w.id}
      rowActions={actionsFor}
      rowActionsTitle={(w) => `Действия со складом ${w.name}`}
      rowSize="double"
      loading={loading}
      error={error}
      onRetry={onRetry}
      empty={{
        icon: Warehouse,
        title: "Складов пока нет",
        description: "Создайте первый — он станет основным, и на него пойдут приёмки.",
      }}
    />
  );
}
